import { useEffect, useState } from 'react';
import { trackScrollDepth, trackTimeOnPage, trackPagePerformance } from '../../utils/eventTracking';

// Thresholds for scroll depth tracking (in percentage)
const SCROLL_THRESHOLDS = [25, 50, 75, 100];

// Time intervals for tracking time on page (in seconds)
const TIME_INTERVALS = [30, 60, 120, 300];

const ScrollDepthTracker = () => {
  const [trackedScrollDepths, setTrackedScrollDepths] = useState([]);
  const [trackedTimeIntervals, setTrackedTimeIntervals] = useState([]);
  const [startTime] = useState(Date.now());

  // Track page load performance
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    
    // Wait for the page to fully load before measuring performance
    window.addEventListener('load', () => {
      // Delay performance tracking slightly to ensure accurate measurements
      setTimeout(() => {
        trackPagePerformance();
      }, 500);
    });
  }, []);

  // Track scroll depth
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      
      if (docHeight === windowHeight) return;
      
      const scrollPercent = Math.floor((scrollTop / (docHeight - windowHeight)) * 100);
      
      SCROLL_THRESHOLDS.forEach(threshold => {
        if (scrollPercent >= threshold && !trackedScrollDepths.includes(threshold)) {
          trackScrollDepth(threshold);
          setTrackedScrollDepths(prev => [...prev, threshold]);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackedScrollDepths]);

  // Track time on page
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const timeInterval = setInterval(() => {
      const timeOnPage = Math.floor((Date.now() - startTime) / 1000);
      
      TIME_INTERVALS.forEach(interval => {
        if (timeOnPage >= interval && !trackedTimeIntervals.includes(interval)) {
          trackTimeOnPage(interval);
          setTrackedTimeIntervals(prev => [...prev, interval]);
        }
      });
      
      // Clear interval after tracking the last time interval
      if (trackedTimeIntervals.length === TIME_INTERVALS.length) {
        clearInterval(timeInterval);
      }
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [startTime, trackedTimeIntervals]);

  // This component doesn't render anything
  return null;
};

export default ScrollDepthTracker;